/**
 * 菜单 + 购物车：localStorage（restaurantMenu / restaurantShoppingCart）。
 * 变更时通过 window.dispatchEvent 派发 restaurantMenuUpdated、restaurantCartUpdated；
 * 并监听 storage，便于同浏览器多标签或 iPad/手机与电脑间通过同源页面刷新数据（依赖同一站点与 localStorage 可见性）。
 * 须在 app.js 之前加载。顾客页按 menu.json 的 menuSavedRev 仅在服务器较新时覆盖本地，避免空 menu.json 冲掉已有餐品。后台 POST 会递增 menuSavedRev。
 */
(function (global) {
  'use strict';

  const STORAGE_KEY = 'restaurantMenu';
  /** 与服务器 menu.json 内 menuSavedRev 对应，避免空文件误覆盖本地已有餐品 */
  const MENU_SERVER_REV_KEY = 'restaurantMenuServerRev';
  const PRESET_VERSION_KEY = 'restaurantMenuPresetVersion';
  const MENU_PRESET_CURRENT = 3;

  /** 整本菜单因体积写入 localStorage 失败时，内存中保留一份供 getMenu() 读取（多见于 iPad + 含大量 base64 的 menu.json） */
  let menuMemoryFallback = null;

  function deepClone(obj) {
    return JSON.parse(JSON.stringify(obj));
  }

  function shallowMenuCopy(m) {
    if (!m || typeof m !== 'object') return getDefaultMenu();
    const out = {
      categories: (m.categories || []).map((c) => (c && typeof c === 'object' ? { ...c } : c)),
      dishes: (m.dishes || []).map((d) => (d && typeof d === 'object' ? { ...d } : d)),
    };
    if (m.ticketStyle && typeof m.ticketStyle === 'object' && !Array.isArray(m.ticketStyle)) out.ticketStyle = { ...m.ticketStyle };
    return out;
  }

  /** 去掉 data: 或过长图链，显著缩小 JSON，提高 setItem 成功率 */
  function stripDishImagesForStorage(menu) {
    const m = normalizeMenu(menu);
    if (!m) return null;
    const stripOne = (d) => {
      if (!d || typeof d !== 'object') return d;
      const next = { ...d };
      const u = next.imageUrl != null ? String(next.imageUrl) : '';
      const im = next.img != null ? String(next.img) : '';
      if (/^data:/i.test(u) || u.length > 600000) next.imageUrl = '';
      if (/^data:/i.test(im) || im.length > 600000) next.img = '';
      return next;
    };
    const slim = {
      categories: m.categories.map((c) => (c && typeof c === 'object' ? { ...c } : c)),
      dishes: m.dishes.map(stripOne),
    };
    if (m.ticketStyle && typeof m.ticketStyle === 'object' && !Array.isArray(m.ticketStyle)) slim.ticketStyle = { ...m.ticketStyle };
    return slim;
  }

  function isStorageQuotaError(e) {
    if (!e) return false;
    if (e.name === 'QuotaExceededError') return true;
    if (e.code === 22) return true;
    return String(e.message || '').toLowerCase().indexOf('quota') !== -1;
  }

  function getDefaultMenu() {
    return { categories: [], dishes: [] };
  }

  function normalizeMenu(raw) {
    if (!raw || typeof raw !== 'object') return null;
    const categories = Array.isArray(raw.categories) ? raw.categories : [];
    const dishes = Array.isArray(raw.dishes) ? raw.dishes : [];
    const out = { categories, dishes };
    if (raw.ticketStyle && typeof raw.ticketStyle === 'object' && !Array.isArray(raw.ticketStyle)) {
      out.ticketStyle = raw.ticketStyle;
    }
    return out;
  }

  function readRawFromStorage() {
    try {
      const raw = global.localStorage.getItem(STORAGE_KEY);
      if (raw === null || raw === '') return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  function readNormalizedFromStorage() {
    return normalizeMenu(readRawFromStorage());
  }

  function writeToStorage(menu) {
    const str = JSON.stringify(menu);
    global.localStorage.setItem(STORAGE_KEY, str);
  }

  function notifyMenuChanged(menu) {
    global.dispatchEvent(new CustomEvent('restaurantMenuUpdated', { detail: menu }));
  }

  function persistAndNotify(menu) {
    const m = normalizeMenu(menu);
    if (!m) return;
    let saved = false;
    try {
      writeToStorage(m);
      saved = true;
      menuMemoryFallback = null;
    } catch (e) {
      if (isStorageQuotaError(e)) {
        try {
          const slim = stripDishImagesForStorage(m);
          if (slim) {
            writeToStorage(slim);
            saved = true;
            menuMemoryFallback = null;
          }
        } catch (_) {
          /* ignore */
        }
      }
      if (!saved) {
        menuMemoryFallback = shallowMenuCopy(m);
      }
    }
    notifyMenuChanged(m);
  }

  global.addEventListener('storage', (e) => {
    if (e.key !== STORAGE_KEY || e.newValue == null) return;
    try {
      const m = normalizeMenu(JSON.parse(e.newValue));
      if (m) notifyMenuChanged(m);
    } catch {
      /* ignore */
    }
  });

  function readPresetVersion() {
    try {
      return parseInt(String(global.localStorage.getItem(PRESET_VERSION_KEY) || 0), 10) || 0;
    } catch {
      return 0;
    }
  }

  function writePresetVersion(v) {
    try {
      global.localStorage.setItem(PRESET_VERSION_KEY, String(v));
    } catch {
      /* ignore */
    }
  }

  function ensureInitialized() {
    const cur = readNormalizedFromStorage();
    const presetVer = readPresetVersion();
    const empty = getDefaultMenu();

    if (!cur) {
      const mem = menuMemoryFallback;
      if (mem && (mem.dishes || []).length > 0) {
        writePresetVersion(MENU_PRESET_CURRENT);
        notifyMenuChanged(mem);
        return mem;
      }
      try {
        writeToStorage(empty);
      } catch (_) {
        menuMemoryFallback = empty;
      }
      writePresetVersion(MENU_PRESET_CURRENT);
      notifyMenuChanged(empty);
      return empty;
    }

    if (presetVer < MENU_PRESET_CURRENT) {
      writePresetVersion(MENU_PRESET_CURRENT);
    }

    return cur;
  }

  function getMenu() {
    let m = readNormalizedFromStorage();
    if (m == null) {
      m = ensureInitialized();
    }
    const diskCount = (m && m.dishes) ? m.dishes.length : 0;
    const mem = menuMemoryFallback;
    const memCount = mem && mem.dishes ? mem.dishes.length : 0;
    if (diskCount === 0 && memCount > 0) {
      return mem;
    }
    return m;
  }

  function setMenu(menu) {
    const m = normalizeMenu(menu);
    if (!m) return;
    persistAndNotify(m);
  }

  function resetMenuToEmpty() {
    const def = getDefaultMenu();
    menuMemoryFallback = null;
    try {
      writeToStorage(def);
    } catch (_) {
      menuMemoryFallback = def;
    }
    writePresetVersion(MENU_PRESET_CURRENT);
    notifyMenuChanged(def);
    return def;
  }

  function categoryIndexById(menu, id) {
    const sid = String(id);
    return menu.categories.findIndex((c) => c && (c.id === id || String(c.id) === sid));
  }

  function dishIndexById(menu, id) {
    const sid = String(id);
    return menu.dishes.findIndex((d) => d && (d.id === id || String(d.id) === sid));
  }

  function addCategory(category) {
    if (!category || typeof category !== 'object') return null;
    const id = category.id != null ? String(category.id).trim() : '';
    if (!id) return null;
    const menu = getMenu();
    const next = deepClone(menu);
    if (categoryIndexById(next, id) !== -1) return null;
    next.categories.push({
      id,
      name: category.name != null ? String(category.name) : id,
    });
    persistAndNotify(next);
    return next;
  }

  function updateCategory(categoryId, partial) {
    if (partial == null || typeof partial !== 'object') return null;
    const menu = getMenu();
    const next = deepClone(menu);
    const ix = categoryIndexById(next, categoryId);
    if (ix === -1) return null;
    const cur = next.categories[ix];
    if (partial.name != null) cur.name = String(partial.name);
    persistAndNotify(next);
    return next;
  }

  function removeCategory(categoryId) {
    const menu = getMenu();
    const next = deepClone(menu);
    const sid = String(categoryId);
    next.categories = next.categories.filter((c) => c && String(c.id) !== sid);
    next.dishes = next.dishes.filter((d) => d && String(d.category) !== sid);
    persistAndNotify(next);
    return next;
  }

  function addDish(dish) {
    if (!dish || typeof dish !== 'object') return null;
    const id = dish.id != null ? String(dish.id).trim() : '';
    const cat = dish.category != null ? String(dish.category).trim() : '';
    if (!id || !cat) return null;
    const menu = getMenu();
    const next = deepClone(menu);
    if (dishIndexById(next, id) !== -1) return null;
    if (categoryIndexById(next, cat) === -1) return null;
    const row = { ...dish };
    row.id = id;
    row.name = dish.name != null ? String(dish.name) : id;
    row.description = dish.description != null ? String(dish.description) : '';
    let pr = Number(dish.price);
    if (!Number.isFinite(pr)) pr = 0;
    row.price = pr;
    row.category = cat;
    row.imageUrl = dish.imageUrl != null ? String(dish.imageUrl) : '';
    next.dishes.push(row);
    persistAndNotify(next);
    return next;
  }

  function updateDish(dishId, partial) {
    if (partial == null || typeof partial !== 'object') return null;
    const menu = getMenu();
    const next = deepClone(menu);
    const ix = dishIndexById(next, dishId);
    if (ix === -1) return null;
    const d = next.dishes[ix];
    if (partial.name != null) d.name = String(partial.name);
    if (partial.description != null) d.description = String(partial.description);
    if (partial.price != null) {
      const p = Number(partial.price);
      d.price = Number.isFinite(p) ? p : d.price;
    }
    if (partial.category != null) {
      const c = String(partial.category).trim();
      if (categoryIndexById(next, c) === -1) return null;
      d.category = c;
    }
    if (partial.imageUrl != null) d.imageUrl = String(partial.imageUrl);
    persistAndNotify(next);
    return next;
  }

  function removeDish(dishId) {
    const menu = getMenu();
    const next = deepClone(menu);
    const sid = String(dishId);
    const before = next.dishes.length;
    next.dishes = next.dishes.filter((d) => d && String(d.id) !== sid);
    if (next.dishes.length === before) return null;
    persistAndNotify(next);
    return next;
  }

  /**
   * 新增或更新菜品（后台表单）：editingId 为空时等价 addDish；非空时在原位置上替换，并允许修改 id（不与其它行冲突）。
   * @returns {object|null} 更新后的完整菜单，失败时 null
   */
  function upsertDish(editingId, row) {
    if (!row || typeof row !== 'object') return null;
    const id = row.id != null ? String(row.id).trim() : '';
    const cat = row.category != null ? String(row.category).trim() : '';
    if (!id || !cat) return null;
    if (!editingId) {
      return addDish(row);
    }
    const menu = getMenu();
    const next = deepClone(menu);
    const oldIx = dishIndexById(next, editingId);
    if (oldIx === -1) return null;
    const eid = String(editingId);
    if (id !== eid && dishIndexById(next, id) !== -1) return null;
    if (categoryIndexById(next, cat) === -1) return null;
    const merged = { ...next.dishes[oldIx], ...row };
    merged.id = id;
    merged.name = row.name != null ? String(row.name) : id;
    merged.description = row.description != null ? String(row.description) : '';
    let pr = Number(row.price);
    if (!Number.isFinite(pr)) pr = 0;
    merged.price = pr;
    merged.category = cat;
    merged.imageUrl = row.imageUrl != null ? String(row.imageUrl) : '';
    next.dishes[oldIx] = merged;
    persistAndNotify(next);
    return next;
  }

  /* —— 购物车 —— */
  const CART_KEY = 'restaurantShoppingCart';

  function normalizeCartLines(raw) {
    if (!Array.isArray(raw)) return [];
    return raw
      .map((row) => {
        if (!row || typeof row !== 'object') return null;
        const dishId = row.dishId != null ? String(row.dishId).trim() : '';
        if (!dishId) return null;
        const q = Number(row.quantity != null ? row.quantity : row.q);
        const quantity = Number.isFinite(q) && q > 0 ? Math.floor(q) : 1;
        return {
          dishId,
          name: row.name != null ? String(row.name) : dishId,
          price: Number.isFinite(Number(row.price)) ? Number(row.price) : 0,
          category: row.category != null ? String(row.category) : '',
          quantity,
        };
      })
      .filter(Boolean);
  }

  function readCartRaw() {
    try {
      const raw = global.localStorage.getItem(CART_KEY);
      if (raw === null || raw === '') return [];
      return normalizeCartLines(JSON.parse(raw));
    } catch {
      return [];
    }
  }

  function writeCart(lines) {
    const list = normalizeCartLines(lines);
    try {
      global.localStorage.setItem(CART_KEY, JSON.stringify(list));
    } catch (e) {
      console.warn('[RestaurantDataLib] cart write failed', e);
    }
    global.dispatchEvent(new CustomEvent('restaurantCartUpdated', { detail: list }));
  }

  global.addEventListener('storage', (e) => {
    if (e.key !== CART_KEY || e.newValue == null) return;
    try {
      const list = normalizeCartLines(JSON.parse(e.newValue));
      global.dispatchEvent(new CustomEvent('restaurantCartUpdated', { detail: list }));
    } catch {
      /* ignore */
    }
  });

  function getCart() {
    return readCartRaw();
  }

  function setCart(lines) {
    writeCart(lines);
    return getCart();
  }

  function clearCart() {
    writeCart([]);
    return [];
  }

  function addCartLine(entry) {
    if (!entry || typeof entry !== 'object') return getCart();
    const dishId = entry.dishId != null ? String(entry.dishId).trim() : '';
    if (!dishId) return getCart();
    const list = readCartRaw();
    const addQ = Number(entry.quantity != null ? entry.quantity : 1);
    const delta = Number.isFinite(addQ) && addQ > 0 ? Math.floor(addQ) : 1;
    const ix = list.findIndex((r) => r.dishId === dishId);
    if (ix === -1) {
      list.push({
        dishId,
        name: entry.name != null ? String(entry.name) : dishId,
        price: Number.isFinite(Number(entry.price)) ? Number(entry.price) : 0,
        category: entry.category != null ? String(entry.category) : '',
        quantity: delta,
      });
    } else {
      list[ix].quantity += delta;
      if (entry.name != null) list[ix].name = String(entry.name);
      if (entry.price != null && Number.isFinite(Number(entry.price))) list[ix].price = Number(entry.price);
      if (entry.category != null) list[ix].category = String(entry.category);
    }
    writeCart(list);
    return list;
  }

  function setCartLineQuantity(dishId, quantity) {
    const id = dishId != null ? String(dishId).trim() : '';
    const q = Number(quantity);
    const list = readCartRaw();
    const ix = list.findIndex((r) => r.dishId === id);
    if (ix === -1) return list;
    if (!Number.isFinite(q) || q <= 0) list.splice(ix, 1);
    else list[ix].quantity = Math.floor(q);
    writeCart(list);
    return list;
  }

  function removeCartLine(dishId) {
    const id = dishId != null ? String(dishId).trim() : '';
    const list = readCartRaw().filter((r) => r.dishId !== id);
    writeCart(list);
    return list;
  }

  global.RestaurantDataLib = {
    STORAGE_KEY,
    MENU_SERVER_REV_KEY,
    PRESET_VERSION_KEY,
    CART_KEY,
    normalizeMenu,
    normalizeCartLines,
    getDefaultMenu,
    ensureInitialized,
    getMenu,
    setMenu,
    resetMenuToEmpty,
    resetToDefaultDemo: resetMenuToEmpty,
    addCategory,
    updateCategory,
    removeCategory,
    addDish,
    updateDish,
    removeDish,
    upsertDish,
    getCart,
    setCart,
    clearCart,
    addCartLine,
    setCartLineQuantity,
    removeCartLine,
  };

  if (global.OS && typeof global.OS.registerModule === 'function') {
    try { global.OS.registerModule('data', global.RestaurantDataLib); } catch (_) {}
  }
})(typeof window !== 'undefined' ? window : globalThis);
